import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import { extractEmployees, taskBelongsToEmployee, cellText, calculatePerformanceRating, normalizeToMs } from "../../utils/helpers";
import CustomSelect from "../../components/CustomSelect";

export default function UsersPage() {
  const { adminRecords, userRecords, larkUsers, deletedUsers, loading } = useData();
  const [search, setSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const companies = useMemo(() => {
    const set = new Set();
    adminRecords.forEach(r => {
      const c = cellText(r.fields?.["Company Name"]);
      if (c && c !== "—") {
        c.split(",").map(x => x.trim()).forEach(x => {
          if (x) set.add(x);
        });
      }
    });
    return Array.from(set).sort();
  }, [adminRecords]);

  const employees = useMemo(() => {
    const now = Date.now();
    const startMs = startDate ? new Date(startDate + "T00:00:00").getTime() : null;
    const endMs = endDate ? new Date(endDate + "T23:59:59").getTime() : null;
    const emps = extractEmployees(adminRecords, { larkUsers, deletedUsers, periodStartMs: startMs, includeAllActive: true });

    // Admin-done lookup: task fully done when all users completed
    const adm = {};
    for (const ar of adminRecords) {
      const tid = cellText(ar.fields?.["Task ID"]);
      if (!tid || tid === "—") continue;
      const st = cellText(ar.fields?.["Overall Status"]).toLowerCase();
      adm[tid] = st.includes("done") || st.includes("✅");
    }
    const isFullyDone = (t) => !!adm[cellText(t.fields?.["Task ID"])];

    return emps.map(emp => {
      // Find tasks assigned to this employee
      const empTasks = userRecords.filter(r => taskBelongsToEmployee(r, emp));

      // Filter tasks by company
      let filteredTasks = empTasks;
      if (selectedCompany !== "all") {
        filteredTasks = filteredTasks.filter(t => {
          const comp = cellText(t.fields?.["Company Name"]);
          return comp && comp.toLowerCase().includes(selectedCompany.toLowerCase());
        });
      }
      
      // Filter tasks by date range (End Date/Due Date is the primary indicator)
      if (startMs || endMs) {
        filteredTasks = filteredTasks.filter(t => {
          const dueTime = normalizeToMs(t.fields?.["End Date"]);
          if (startMs && (!dueTime || dueTime < startMs)) return false;
          if (endMs && (!dueTime || dueTime > endMs)) return false;
          return true;
        });
      }

      // Calculate statistics — "done" means all assigned users completed
      const total = filteredTasks.length;
      const done = filteredTasks.filter(t => isFullyDone(t)).length;
      const overdue = filteredTasks.filter(t => !isFullyDone(t) && normalizeToMs(t.fields?.["End Date"]) > 0 && normalizeToMs(t.fields?.["End Date"]) < now).length;
      const inProgress = filteredTasks.filter(t => !isFullyDone(t) && (normalizeToMs(t.fields?.["End Date"]) === 0 || normalizeToMs(t.fields?.["End Date"]) >= now) && (cellText(t.fields?.["Overall Status"]).toLowerCase().includes("progress") || cellText(t.fields?.["Overall Status"]).toLowerCase().includes("🟡"))).length;
      const notStarted = filteredTasks.filter(t => !isFullyDone(t) && (normalizeToMs(t.fields?.["End Date"]) === 0 || normalizeToMs(t.fields?.["End Date"]) >= now) && !(cellText(t.fields?.["Overall Status"]).toLowerCase().includes("progress") || cellText(t.fields?.["Overall Status"]).toLowerCase().includes("🟡"))).length;

      // Calculate performance rating
      const rating = calculatePerformanceRating(filteredTasks, adminRecords);

      return {
        ...emp,
        total,
        done,
        overdue,
        inProgress,
        notStarted,
        rating
      };
    });
  }, [adminRecords, userRecords, larkUsers, deletedUsers, selectedCompany, startDate, endDate]);

  const filtered = employees.filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading && adminRecords.length === 0) return <div className="loading-box"><div className="spinner" /><span>Loading...</span></div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>👥 Employees</h1>
        <p>{employees.length} team members in the system</p>
      </div>

      <div className="toolbar" style={{ marginBottom: 20, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div className="search-wrapper" style={{ flex: 2, minWidth: 200 }}>
          <span className="search-icon">🔍</span>
          <input className="search-input" style={{ width: "100%" }} placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        
        <div style={{ flex: 1.5, minWidth: 180 }}>
          <CustomSelect 
            placeholder="All Companies"
            options={companies}
            value={selectedCompany === "all" ? "" : selectedCompany}
            onChange={val => setSelectedCompany(val || "all")}
            isClearable={true}
          />
        </div>

        <div className="date-range-group" style={{ flex: 2 }}>
          <input 
            type="date" 
            className={`date-input ${startDate ? "has-value" : ""}`}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
          <span style={{ color: "var(--gray-400)", fontSize: 12 }}>to</span>
          <input 
            type="date" 
            className={`date-input ${endDate ? "has-value" : ""}`}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </div>
      </div>

      <div className="employee-grid">
        {filtered.map(emp => (
          <div key={emp.name} className="employee-card" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div className="avatar avatar-employee avatar-lg">
                  {emp.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "var(--gray-900)", fontSize: 15 }}>{emp.name}</div>
                  <div style={{ fontSize: 12, color: "var(--gray-400)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {emp.email || "No email on record"}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span className="badge badge-info" style={{ fontSize: 10 }}>
                      {emp.total} task{emp.total !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="employee-mini-stats">
                {[
                  { label: "Done", value: emp.done, color: "var(--success)" },
                  { label: "Progress", value: emp.inProgress, color: "var(--warning)" },
                  { label: "Overdue", value: emp.overdue, color: "var(--danger)" },
                  { label: "Pending", value: emp.notStarted, color: "var(--gray-500)" },
                ].map(s => (
                  <div key={s.label} className="employee-mini-stat">
                    <div className="employee-mini-stat-label">{s.label}</div>
                    <div className="employee-mini-stat-value" style={{ color: s.value === 0 ? "var(--gray-300)" : s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="employee-card-rating" style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--gray-100)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "var(--gray-500)", fontWeight: 500 }}>Performance Rating</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: emp.total === 0 ? "var(--gray-400)" : (emp.rating >= 7 ? "var(--success)" : (emp.rating >= 4 ? "var(--warning)" : "var(--danger)")) }}>
                {emp.total > 0 ? `${emp.rating.toFixed(2)} / 10` : "—"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
